import * as React from 'react';
import { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { USER_STATUS } from '@careerstart/wae-common/src/main/constants/USER_EDITABLE_FIELDS';
import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import { Box, Card, CardContent, CardHeader, Divider, Typography } from '@mui/material';

import CopyLinkIcon from '../../../assets/icons/CopyLinkIcon';
import {
  selectUpdateUserAddressIsProcessing,
  selectUpdateUserNameIsProcessing,
  selectUpdateUserPhoneNumberIsProcessing,
} from '../../../store/selectors/userListSelector';
import theme from '../../../theme';
import { PRIMARY_PURPLE } from '../../../theme/colorConstants';
import { PRIMARY_FONT } from '../../../theme/fontConstants';
import {
  updateUserAddress,
  updateUserName,
  updateUserPhoneNumber,
} from '../../../views/usersList/usersListReducer';
import { CANDIDATE_STATUS_CONFIG } from '../constant';
import { convertPreferredTimesFromApiToUiFormat } from '../helpers';
import useCandidateReliabilityDisplay from '../useCandidateReliabilityDisplay';

import PersonalIdentifyingInfoModal from './PersonalIdentifyingInfoModal/PersonalIdentifyingInfoModal';
import AgencyCard from './AgencyCard';
import Details from './Details';
import EditableAddressField from './EditableAddressField';
import EditableEmailField from './EditableEmailField';
import EditableNameField from './EditableNameField';
import EditablePhoneField from './EditablePhoneField';
import UserDetailAvatar from './UserDetailAvatar';

const styles = theme?.components?.userCard;

const StatusLine = ({ labelKey, statusValue }) => {
  const statusConfig = CANDIDATE_STATUS_CONFIG[statusValue];
  const label = LanguageConverterV2(labelKey);
  const translatedStatus = LanguageConverterV2(statusConfig?.displayName ?? labelKey);
  const statusLabel = statusConfig ? translatedStatus : statusValue || '—';

  return (
    <Box display="flex" alignItems="center" gap={1}>
      <Typography sx={{ fontFamily: PRIMARY_FONT[500] }}>{label}</Typography>
      {statusConfig?.icon}
      <Typography sx={{ fontFamily: PRIMARY_FONT[500], color: statusConfig?.color }}>
        {statusLabel}
      </Typography>
    </Box>
  );
};

StatusLine.propTypes = {
  labelKey: PropTypes.string.isRequired,
  statusValue: PropTypes.string,
};

const EditableCandidateCard = ({ user, onCopyId, onClick }) => {
  const dispatch = useDispatch();
  const isUpdateUserAddressProcessing = useSelector(selectUpdateUserAddressIsProcessing);
  const isUpdateUserNameProcessing = useSelector(selectUpdateUserNameIsProcessing);
  const isUpdateUserPhoneNumberProcessing = useSelector(selectUpdateUserPhoneNumberIsProcessing);
  const isUserDeactivated = user?.status === USER_STATUS.DEACTIVATED;
  const reliabilityDisplay = useCandidateReliabilityDisplay(user?.candidate?.metrics);
  const updatedPreferredShiftTimes = user?.candidate?.preferredShiftTimes
    ? convertPreferredTimesFromApiToUiFormat(user?.candidate?.preferredShiftTimes)
    : [];
  const userInfo = {
    status: user?.status,
    role: user?.role,
    email: user?.email,
    name: user?.name,
    lastLogin: user?.lastLoginDate || null,
    ...reliabilityDisplay,
    attendance: user?.rating?.strikeOuts?.length > 0 ? 3 : user?.rating?.infractions?.length,
    preferredShiftTimes: updatedPreferredShiftTimes,
    industry: user?.candidate?.industry,
    phoneNumber: user?.phoneNumber,
    address: user?.candidate?.preferredLocation,
    agency: user?.candidate?.agency?.name || 'Test Agency',
  };

  const [isPiiModalOpen, setIsPiiModalOpen] = useState(false);

  const handleAddressSave = ({ userId, address }) => {
    dispatch(updateUserAddress({ userId, address }));
  };

  const handleNameSave = ({ userId, name }) => {
    dispatch(updateUserName({ userId, name }));
  };

  const handlePhoneSave = ({ userId, phoneNumber }) => {
    dispatch(updateUserPhoneNumber({ userId, phoneNumber }));
  };

  return (
    <Card
      sx={{ ...styles.cardStyle, height: 'auto' }}
      onClick={onClick}
      data-testid="editable-candidate-card"
    >
      <CardHeader
        sx={styles.cardHeader}
        title={
          <Box display="flex" flexDirection="column" gap={0.5}>
            <StatusLine labelKey="userCard.header.statusV1" statusValue={userInfo?.status} />
            <StatusLine
              labelKey="userCard.header.statusV2"
              statusValue={user?.state?.statusV2?.status}
            />
          </Box>
        }
      />
      <Divider />
      <CardContent>
        <Box display="flex" flexDirection="column" gap={2}>
          <Box display="flex" sx={{ alignItems: 'center', gap: 2, justifyContent: 'center' }}>
            <UserDetailAvatar user={user} />
          </Box>

          <Box
            display="flex"
            sx={{ alignItems: 'center', gap: 2, justifyContent: 'space-between' }}
          >
            <Box width="100%" display="flex" flexDirection="column" gap={1}>
              <EditableNameField
                initialValue={userInfo?.name}
                userId={user?.id}
                disabled={isUserDeactivated}
                isLoading={isUpdateUserNameProcessing}
                onNameSave={handleNameSave}
              />
              <Box
                display="flex"
                alignItems="center"
                onClick={onCopyId}
                sx={{ cursor: 'pointer', gap: 1 }}
              >
                <Typography sx={styles.copyUser}>
                  {LanguageConverterV2('userCard.subTitle.copyUserId')}
                </Typography>
                <CopyLinkIcon />
              </Box>
            </Box>
          </Box>
        </Box>
        <Box mt={2}>
          <EditablePhoneField
            initialValue={userInfo?.phoneNumber}
            userId={user?.id}
            disabled={isUserDeactivated}
            isLoading={isUpdateUserPhoneNumberProcessing}
            onPhoneSave={handlePhoneSave}
          />
          <EditableEmailField
            initialValue={userInfo?.email}
            userId={user?.id}
            userStatus={user?.status}
            disabled={isUserDeactivated}
          />
          <EditableAddressField
            initialValue={userInfo?.address}
            userId={user?.id}
            disabled={isUserDeactivated}
            isLoading={isUpdateUserAddressProcessing}
            onAddressSave={handleAddressSave}
          />
        </Box>
        <Box sx={{ mt: 2, mb: 1 }}>
          <Typography sx={styles.subTitleStyle}>
            {LanguageConverterV2('userCard.personalIdentifyingInfo')}
          </Typography>
          <Typography
            data-testid="pii-view-info-trigger"
            sx={{
              color: PRIMARY_PURPLE[70],
              textDecoration: 'underline',
              cursor: 'pointer',
              fontFamily: PRIMARY_FONT[400],
              fontSize: '14px',
            }}
            onClick={() => setIsPiiModalOpen(true)}
          >
            {LanguageConverterV2('userCard.viewInfo')}
          </Typography>
        </Box>
        <AgencyCard
          agency={user?.candidate?.agency}
          userRecruiter={user?.candidate?.recruiter}
          userName={userInfo?.name}
          userRole={userInfo?.role}
          userId={user.id}
          externalAgencyId={user?.candidate?.externalAgencyId}
        />
        <Details userInfo={userInfo} isEditable />
      </CardContent>
      {isPiiModalOpen && (
        <PersonalIdentifyingInfoModal
          isOpen={isPiiModalOpen}
          onClose={() => setIsPiiModalOpen(false)}
          userId={user?.id}
        />
      )}
    </Card>
  );
};
EditableCandidateCard.propTypes = {
  user: PropTypes.shape({
    candidate: PropTypes.shape({
      agency: PropTypes.shape({ name: PropTypes.string }),
      externalAgencyId: PropTypes.string,
      recruiter: PropTypes.shape({ name: PropTypes.string }),
      preferredShiftTimes: PropTypes.shape({}),
      preferredLocation: PropTypes.shape({
        city: PropTypes.string,
        state: PropTypes.string,
      }),
      industry: PropTypes.arrayOf(PropTypes.string),
      metrics: PropTypes.shape({
        reliabilityScore: PropTypes.number,
      }),
    }),
    role: PropTypes.string,
    status: PropTypes.string,
    state: PropTypes.shape({
      statusV2: PropTypes.shape({
        status: PropTypes.string,
      }),
    }),
    email: PropTypes.string,
    name: PropTypes.string,
    id: PropTypes.string,
    lastLoginDate: PropTypes.string,
    reliabilityScore: PropTypes.number,
    attendance: PropTypes.number,
    phoneNumber: PropTypes.string,
    location: PropTypes.string,
    profilePictureId: PropTypes.string,
    profilePictureUrl: PropTypes.string,
    rating: PropTypes.shape({
      strikeOuts: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.shape({}))),
      infractions: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.shape({}))),
    }),
  }),
  onCopyId: PropTypes.func,
  onClick: PropTypes.func,
};

export default EditableCandidateCard;
